import axios from 'axios';
import { EntitlementProductEntity } from 'Models/Entities';
import { ENTITLEMENT_PRODUCT_API_URL } from 'Constants';
import { EntitlementProductType } from 'Models/Enums';
import { ProductPriceOverridePayload } from './PosProductApi';

export interface CreateEntitlementProductRequest {
	name: string;
	productType: EntitlementProductType;
	price: number;
	isDisabled?: boolean;
	isRestricted?: boolean;
	defaultExpiryDurationDays?: number;
	order?: number;
	termsAndConditions?: string | null;
	productDefinition: string;
	priceOverrides?: ProductPriceOverridePayload[];
}

export interface UpdateEntitlementProductRequest extends CreateEntitlementProductRequest {}

export interface PagedResult<T> {
	items: T[];
	total: number;
	page: number;
	pageSize: number;
}

export class ConcurrencyConflictError extends Error {
	constructor(message: string) {
		super(message);
		this.name = 'ConcurrencyConflictError';
	}
}

export const fetchProducts = async (page = 1, pageSize = 20): Promise<PagedResult<EntitlementProductEntity>> => {
	const { data } = await axios.get(ENTITLEMENT_PRODUCT_API_URL, {
		params: { page, pageSize },
	});

	return {
		...data,
		items: data.items.map((item: any) => new EntitlementProductEntity(item)),
	};
};

export const fetchProductById = async (id: string): Promise<EntitlementProductEntity> => {
	const response = await axios.get(`${ENTITLEMENT_PRODUCT_API_URL}/${id}`);
	return new EntitlementProductEntity(response.data);
};

export const createProduct = async (
	productData: CreateEntitlementProductRequest,
): Promise<EntitlementProductEntity> => {
	const response = await axios.post(ENTITLEMENT_PRODUCT_API_URL, productData);
	return new EntitlementProductEntity(response.data);
};

export const updateProduct = async (
	id: string,
	productData: UpdateEntitlementProductRequest,
): Promise<EntitlementProductEntity> => {
	try {
		const response = await axios.put(`${ENTITLEMENT_PRODUCT_API_URL}/${id}`, productData);
		return new EntitlementProductEntity(response.data);
	} catch (error: any) {
		if (error.response?.status === 409) {
			throw new ConcurrencyConflictError(error.response.data || 'The product has been modified by another user.');
		}
		throw error;
	}
};

export const deleteProduct = async (id: string): Promise<void> => {
	try {
		await axios.delete(`${ENTITLEMENT_PRODUCT_API_URL}/${id}`);
	} catch (error: any) {
		if (error.response?.status === 400) {
			throw new Error(error.response.data || 'Cannot delete product with existing dependencies.');
		}
		throw error;
	}
};
